import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';

export type OnboardingState = {
  region?: 'UK' | 'EU' | 'US' | 'Canada';
  debtSituation?: 'high_debt' | 'some_debt' | 'low_debt' | 'no_debt';
  emergencySavings?: 'no_savings' | 'partial_savings' | 'good_savings' | 'excellent_savings';
  investmentExperience?: 'no_investments' | 'beginner' | 'moderate' | 'experienced';
  incomeStability?: 'unstable' | 'somewhat_stable' | 'stable' | 'multiple_streams';
  financialGoal?: 'survive' | 'budget' | 'grow' | 'freedom';
  savingsRate?: 'sr_0_5' | 'sr_6_10' | 'sr_11_20' | 'sr_gt_20';
  budgetTracking?: 'never' | 'sometimes' | 'most_months' | 'every_month';
  retakeMode?: boolean;
  /** Set to true after quiz_complete_write succeeds — lets the guard skip the redirect */
  quizJustCompleted?: boolean;
};

type OnboardingStateContextType = {
  state: OnboardingState;
  /** Merge updates into existing state (ignores invalid payloads) */
  updateState: (updates: Partial<OnboardingState>) => void;
  /** Replace the entire state */
  replaceState: (next: OnboardingState) => void;
  /** Reset state back to empty object */
  resetState: () => void;
};

const OnboardingStateContext = createContext<OnboardingStateContextType | undefined>(undefined);

// Narrow plain object at runtime to avoid crashes if events are accidentally passed
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

type OnboardingStateProviderProps = {
  children: ReactNode;
};

export function OnboardingStateProvider({ children }: OnboardingStateProviderProps) {
  const [state, setState] = useState<OnboardingState>({});

  const updateState = useCallback((updates: Partial<OnboardingState>) => {
    if (!isPlainObject(updates)) {
      // In dev we can warn; in prod we silently ignore to avoid UI crashes
      if (process.env.NODE_ENV !== 'production') {
        // console.warn('updateState ignored non-object payload:', updates);
      }
      return;
    }
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const replaceState = useCallback((next: OnboardingState) => {
    setState(isPlainObject(next) ? (next as OnboardingState) : {});
  }, []);

  const resetState = useCallback(() => setState({}), []);

  const value = useMemo<OnboardingStateContextType>(
    () => ({ state, updateState, replaceState, resetState }),
    [state, updateState, replaceState, resetState]
  );

  return (
    <OnboardingStateContext.Provider value={value}>
      {children}
    </OnboardingStateContext.Provider>
  );
}

export function useOnboardingStateContext() {
  const context = useContext(OnboardingStateContext);
  if (!context) {
    throw new Error('useOnboardingStateContext must be used within OnboardingStateProvider');
  }
  return context;
}
